import { getPortalLogo, merchantDineSavvyLogo } from '@assets/logos';
import env from '@config/env';
import useMediaQuery from '@hooks/useMediaQuery';
import { memo } from 'react';

/** Official-style App Store badge (black). */
const AppStoreBadge = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 120 40"
    className="h-10 w-auto"
    aria-hidden="true"
  >
    <rect width="120" height="40" rx="6" fill="#000" />
    <path
      fill="#fff"
      d="M24.77 20.3c-.03-2.27 1.85-3.36 1.94-3.41-1.06-1.55-2.7-1.76-3.28-1.78-1.4-.14-2.73.82-3.43.82-.71 0-1.8-.8-2.96-.78-1.52.02-2.92.89-3.7 2.25-1.58 2.74-.4 6.8 1.14 9.03.76 1.09 1.65 2.31 2.83 2.27 1.14-.05 1.56-.73 2.94-.73 1.37 0 1.76.73 2.96.71 1.23-.02 2-.1.11 2.75 1.22-1.26 1.22-2.49 1.24-2.55-.03-.01-2.38-.91-2.4-3.61zm-2.25-6.65c.63-.76 1.05-1.81.93-2.86-.9.04-2 .6-2.65 1.36-.58.67-1.09 1.75-.96 2.78 1.01.08 2.04-.51 2.68-1.28z"
    />
    <text
      x="36"
      y="15.5"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="7"
      fontWeight="400"
    >
      Download on the
    </text>
    <text
      x="36"
      y="28.5"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="12"
      fontWeight="600"
    >
      App Store
    </text>
  </svg>
);

/** Official-style Google Play badge (black). */
const GooglePlayBadge = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 135 40"
    className="h-10 w-auto"
    aria-hidden="true"
  >
    <rect width="135" height="40" rx="6" fill="#000" />
    <path
      d="M12.4 8.55a1.4 1.4 0 0 0-.25.82v21.26c0 .5.09.98.25 1.37L24.1 20 12.4 8.55z"
      fill="#00D4FF"
    />
    <path
      d="M28.55 16.55 15.15 8.35a1.4 1.4 0 0 0-.99-.14L24.1 20l3.45-3.45z"
      fill="#00F076"
    />
    <path
      d="M28.55 23.45 24.1 20 12.4 31.45c.29.13.64.07.99-.17l15.16-7.83z"
      fill="#F4364C"
    />
    <path
      d="M31.95 18.35 28.55 16.55 24.1 20l3.45 3.45 3.4-2.05c1.03-.62 1.03-2.13 0-2.75z"
      fill="#FFCA00"
    />
    <text
      x="40"
      y="15.5"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="7"
      fontWeight="400"
      letterSpacing="0.4"
    >
      GET IT ON
    </text>
    <text
      x="40"
      y="28.5"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="12"
      fontWeight="600"
    >
      Google Play
    </text>
  </svg>
);

const isAdminPortal = env.APP_TYPE === 'admin';

/**
 * Blocks the web app below the 768px breakpoint.
 * Merchant/business-user: App Store / Google Play (Figma 40000679:26896).
 * Admin: desktop-only message — no store badges.
 */
const MobileAppGate = memo(({ children }) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (!isMobile) {
    return children;
  }

  const logo =
    env.APP_TYPE === 'merchant' || isAdminPortal
      ? merchantDineSavvyLogo
      : getPortalLogo(env.APP_TYPE);

  const message = isAdminPortal
    ? 'Admin panel is not available on phone screens. Please use a desktop device.'
    : 'Get the app to access and manage your businesses on your phone.';

  return (
    <div
      className={`flex min-h-screen w-full flex-col items-center gap-12 bg-[#f5f5f5] px-6 py-10 text-center ${
        isAdminPortal ? '' : 'justify-center'
      }`}
    >
      <div className="flex aspect-square w-full max-w-[280px] items-center justify-center rounded-2xl bg-white bg-[radial-gradient(#e4e4e4_1.25px,transparent_1.25px)] [background-size:16px_16px]">
        <img src={logo} alt={env.APP_NAME} className="h-[88px] w-auto shrink-0" />
      </div>

      <p className="max-w-[260px] text-[18px] font-semibold leading-[26px] tracking-normal text-[#171717]">
        {message}
      </p>

      {isAdminPortal ? null : (
        <div className="flex items-center justify-center gap-3">
          <a
            href={`${env.APP_STORE_URL}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
            className="inline-flex shrink-0 transition-opacity hover:opacity-85"
          >
            <AppStoreBadge />
          </a>
          <a
            href={`${env.PLAY_STORE_URL}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
            className="inline-flex shrink-0 transition-opacity hover:opacity-85"
          >
            <GooglePlayBadge />
          </a>
        </div>
      )}
    </div>
  );
});

MobileAppGate.displayName = 'MobileAppGate';

export default MobileAppGate;
